import { Loc, t } from "@/locales";
import { FieldLabel } from "@/shared/presentation/components/FieldLabel";
import { OptionGrid } from "@/shared/presentation/components/OptionGrid";
import type { OptionGridVariant } from "@/shared/presentation/components/OptionGrid";
import { SelectField } from "@/shared/presentation/components/SelectField";
import { SwatchRow } from "@/shared/presentation/components/SwatchRow";
import { useOptionsPanelComponent } from "@/configurator/presentation/useOptionsPanelComponent";
import type { OptionGroupView } from "@/configurator/domain/models/view";

// Which primitive draws a group. `gridOptions` is the multi-select grid; the
// single-select kinds differ only in the grid modifier they carry.
const GRID_VARIANT: Record<string, OptionGridVariant> = {
  toggle: "material",
  grid: "armrest",
  gridOptions: "accessory",
};

type GroupProps = {
  group: OptionGroupView;
  onPick: (groupId: string, optionId: string) => void;
  onToggle: (groupId: string, optionId: string) => void;
};

function OptionGroupControl({ group, onPick, onToggle }: GroupProps) {
  if (group.kind === "select") {
    return (
      <SelectField
        items={group.items}
        onSelect={(optionId) => onPick(group.id, optionId)}
      />
    );
  }

  if (group.kind === "swatches") {
    return (
      <SwatchRow
        items={group.items}
        onSelect={(optionId) => onPick(group.id, optionId)}
      />
    );
  }

  return (
    <OptionGrid
      variant={GRID_VARIANT[group.kind] ?? "material"}
      multiple={group.multiple}
      items={group.items}
      onSelect={(optionId) =>
        group.multiple
          ? onToggle(group.id, optionId)
          : onPick(group.id, optionId)
      }
    />
  );
}

export function OptionsPanel() {
  const { groups, models, showModels, pickOption, toggleOption, pickModel } =
    useOptionsPanelComponent();

  return (
    <div className="options-panel">
      {showModels ? (
        <div className="options-panel__group options-panel__group--models">
          <FieldLabel>{t(Loc.modelGroupLabel)}</FieldLabel>
          <OptionGrid variant="model" items={models} onSelect={pickModel} />
        </div>
      ) : null}

      {groups.map((group) => (
        <div
          key={group.id}
          className={[
            "options-panel__group",
            group.kind === "swatches" ? "options-panel__group--swatches" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <FieldLabel>{group.label}</FieldLabel>
          <OptionGroupControl
            group={group}
            onPick={pickOption}
            onToggle={toggleOption}
          />
        </div>
      ))}
    </div>
  );
}
